import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import { Tooltip } from "@fluentui/react-components";
import userService from "../../../store/services/user.service";
import { generalActions } from "../../../store/actions";
import Spin from "../spinner/Spin";
import { dateOnlyFormatted } from "../../../utils/systemSettings";
import { ArrowSortIcon } from "../../../modules/default-svg";
import { compareArrays, sortColumnsByOrderlist } from "../../../helpers/utils";
import { useColumnConfig } from "./ColumnConfigProvider";

const HealthStatusTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    loading
  } = props;
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(loading);
  const setting = JSON.parse(localStorage.getItem("settings"));
  const tableContainerRef = useRef(null);
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [sortingCriteria, setSortingCriteria] = useState({
    column: null,
    order: "asc",
  });
  const { getColumnConfigs } = useColumnConfig();
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (
      rows &&
      rows.length > 0 &&
      sortingCriteria.column &&
      sortingCriteria.order
    ) {
      const sortData = [...rows].sort((a, b) => {
        let valueA = "";
        let valueB = "";

        switch (sortingCriteria.column) {
          case "projectName":
          case "programName":
            valueA = a.name || "";
            valueB = b.name || "";
            break;
          default:
            valueA = a[sortingCriteria.column] || "";
            valueB = b[sortingCriteria.column] || "";
        }

        valueA = valueA.toString();
        valueB = valueB.toString();

        if (sortingCriteria.order === "asc") {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      });

      setSortedData(sortData);
    } else {
      setSortedData(rows || []);
    }
  }, [rows, sortingCriteria]);

  const handleSort = (columnId) => {
    setSortingCriteria((prev) => {
      if (prev.column !== columnId) {
        return {
          column: columnId,
          order: "asc",
        };
      }

      switch (prev.order) {
        case null:
          return {
            column: columnId,
            order: "asc",
          };
        case "asc":
          return {
            column: columnId,
            order: "desc",
          };
        case "desc":
          return {
            column: null,
            order: null,
          };
        default:
          return {
            column: columnId,
            order: "asc",
          };
      }
    });
  };

  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => {})
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const parseDate = (str) => {
    const dateString = str.toString();
    const year = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const day = dateString.slice(4, 6);
    const fullYear = `20${year}`;
    const date = new Date(`${month}-${day}-${fullYear}`);
    return dateOnlyFormatted(setting, date);
  };

  const getStatus = (type) => {
    switch (type) {
      case 1:
        return "#EAA300";
      case 2:
        return "#C50F1F";
      default:
        return "#0B6A0B";
    }
  };

  const renderStatusCells = (statusArray) => (
    <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
      {statusArray?.map((status, index) => (
        <Tooltip
          key={index}
          content={`${status.period ? parseDate(status.period) : ""} ${
            status.description ? `: ${status.description}` : ""
          }`}
        >
          {status.isForecast ? (
            status.status === 0 ? (
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0371 1.43492C10.7621 1.15643 10.3892 1 10.0005 1C9.61176 1 9.23893 1.15643 8.96388 1.43492L3.42978 7.04242C3.1546 7.3214 3 7.69978 3 8.09432C3 8.48886 3.1546 8.86724 3.42978 9.14622C3.70496 9.42521 4.07819 9.58194 4.46736 9.58194C4.85653 9.58194 5.22976 9.42521 5.50495 9.14623L8.53359 6.07677L8.53359 17.5129C8.53359 17.9073 8.68814 18.2855 8.96324 18.5644C9.23833 18.8433 9.61144 19 10.0005 19C10.3895 19 10.7626 18.8433 11.0377 18.5644C11.3128 18.2855 11.4674 17.9073 11.4674 17.5129L11.4674 6.07677L14.4951 9.14623C14.6313 9.28436 14.7931 9.39394 14.9711 9.4687C15.1491 9.54346 15.3399 9.58194 15.5326 9.58194C15.7253 9.58194 15.9161 9.54346 16.0942 9.4687C16.2722 9.39394 16.434 9.28436 16.5702 9.14623C16.7065 9.00809 16.8146 8.84409 16.8883 8.66361C16.962 8.48312 17 8.28968 17 8.09432C17 7.89897 16.962 7.70552 16.8883 7.52504C16.8146 7.34455 16.7065 7.18056 16.5702 7.04242L11.0371 1.43492Z"
                  fill="#0B6A0B"
                />
              </svg>
            ) : status.status === 1 ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.5651 8.0371C17.8436 7.76205 18 7.38922 18 7.00049C18 6.61176 17.8436 6.23893 17.5651 5.96388L11.9576 0.429781C11.6786 0.154597 11.3002 -2.96957e-07 10.9057 -3.10103e-07C10.5111 -3.23248e-07 10.1328 0.154597 9.85378 0.429781C9.57479 0.704965 9.41806 1.07819 9.41806 1.46736C9.41806 1.85653 9.57479 2.22976 9.85377 2.50495L12.9232 5.53359L1.48714 5.53359C1.09273 5.53359 0.714466 5.68814 0.435573 5.96324C0.156681 6.23833 -2.88995e-07 6.61144 -3.06001e-07 7.00049C-3.23007e-07 7.38953 0.156681 7.76264 0.435573 8.03774C0.714466 8.31284 1.09273 8.46738 1.48714 8.46738L12.9232 8.46738L9.85377 11.4951C9.71564 11.6313 9.60606 11.7931 9.5313 11.9711C9.45654 12.1491 9.41806 12.3399 9.41806 12.5326C9.41806 12.7253 9.45654 12.9161 9.5313 13.0942C9.60606 13.2722 9.71564 13.434 9.85377 13.5702C9.99191 13.7065 10.1559 13.8146 10.3364 13.8883C10.5169 13.962 10.7103 14 10.9057 14C11.101 14 11.2945 13.962 11.475 13.8883C11.6554 13.8146 11.8194 13.7065 11.9576 13.5702L17.5651 8.0371Z"
                  fill="#EAA300"
                />
              </svg>
            ) : (
              <svg
                width="14"
                height="18"
                viewBox="0 0 14 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.9629 17.5651C6.23795 17.8436 6.61078 18 6.99951 18C7.38824 18 7.76107 17.8436 8.03612 17.5651L13.5702 11.9576C13.8454 11.6786 14 11.3002 14 10.9057C14 10.5111 13.8454 10.1328 13.5702 9.85378C13.295 9.57479 12.9218 9.41806 12.5326 9.41806C12.1435 9.41806 11.7702 9.57479 11.4951 9.85377L8.46641 12.9232L8.46641 1.48714C8.46641 1.09272 8.31186 0.714466 8.03676 0.435573C7.76167 0.156681 7.38856 -2.88995e-07 6.99951 -3.06001e-07C6.61047 -3.23007e-07 6.23736 0.156681 5.96226 0.435573C5.68716 0.714466 5.53262 1.09272 5.53262 1.48714L5.53262 12.9232L2.50495 9.85377C2.36869 9.71564 2.20693 9.60606 2.0289 9.5313C1.85087 9.45654 1.66006 9.41806 1.46736 9.41806C1.27467 9.41806 1.08386 9.45654 0.905829 9.5313C0.7278 9.60606 0.566038 9.71564 0.42978 9.85377C0.293523 9.99191 0.185439 10.1559 0.111698 10.3364C0.0379556 10.5169 3.18642e-07 10.7103 3.10103e-07 10.9057C3.01563e-07 11.101 0.0379556 11.2945 0.111697 11.475C0.185439 11.6554 0.293523 11.8194 0.42978 11.9576L5.9629 17.5651Z"
                  fill="#C50F1F"
                />
              </svg>
            )
          ) : (
            <svg
              width="12"
              height="18"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="6" cy="6" r="6" fill={getStatus(status.status)} />
            </svg>
          )}
        </Tooltip>
      ))}
    </div>
  );

  const getSortableColumns = (columnsList) => {
    return columnsList
      .filter((col) => col.label === "Program" || col.label === "Project")
      .map((col) => col.accessorKey);
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;
    const sortableColumnKeys = getSortableColumns(colsList);
    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(columnsOrder);
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableSorting: false,
          sortingFn: false,
          Cell: ({ cell, row }) => {
            const item = row.original;
            switch (col.label) {
              case "Status":
                return renderStatusCells(item.healthStatus.global);
              case "Program":
                return (
                  <span
                    onClick={() => {
                      props.dashboardFilterSetId(item.id);
                      props.dashboardFilterSetName(item.name);
                    }}
                  >
                    {item.name}
                  </span>
                );
              case "Project":
                return (
                  <span
                    onClick={() => {
                      props.dashboardFilterSetId(item.id);
                      props.dashboardFilterSetName(item.name);
                    }}
                  >
                    {item.name}
                  </span>
                );
              case "Scope":
                return renderStatusCells(item.healthStatus.scope);
              case "Time":
                return renderStatusCells(item.healthStatus.time);
              case "Cost":
                return renderStatusCells(item.healthStatus.cost);
              case "Quality":
                return renderStatusCells(item.healthStatus.quality);
              case "Issues":
                return renderStatusCells(item.healthStatus.issues_Concerns);
              case "Risks":
                return renderStatusCells(item.healthStatus.risks);
              case "Resources":
                return renderStatusCells(item.healthStatus.resources_Team);
              default:
                return cell.getValue();
            }
          },
          Header: ({ column }) => {
            const isSortable = sortableColumnKeys.includes(column.id);
            const isActivelySorted = sortingCriteria.column === column.id;
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: isSortable ? "pointer" : "default",
                  userSelect: "none",
                }}
                onClick={() => isSortable && handleSort(column.id)}
              >
                <span>{column.columnDef.header}</span>
                {isSortable && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: isSortable ? "pointer" : "default",
                      userSelect: "none",
                      width: "100%",
                      opacity: isActivelySorted ? 1 : 0.5,
                      justifyContent: "space-between",
                      marginLeft: "8px",
                    }}
                  >
                    <ArrowSortIcon
                      isActive={isActivelySorted}
                      sortDirection={
                        isActivelySorted ? sortingCriteria.order : null
                      }
                    />
                  </span>
                )}
              </div>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });

    const uniqueRows =
      rowData?.filter(
        (row, index, self) => index === self.findIndex((r) => r.id === row.id)
      ) || [];
    setRows((prevRows) => {
      if (JSON.stringify(prevRows) !== JSON.stringify(uniqueRows)) {
        return uniqueRows;
      }
      return prevRows;
    });
  }, [colsList, rowData, editingHeader, headerValue, sortingCriteria]);


  const table = useMaterialReactTable({
    columns: cols,
    data: sortedData,
    enableHiding: true,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableSorting: false,
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    positionGlobalFilter: "left",
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        type="HealthStatus"
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        rightPane={false}
        healthData={sortedData}
        isSearchDisable={cols.length}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {isLoading ? (
            <div className="loading-indicator">
              <span>Loading more data...</span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rows.length}</strong> of{" "}
                <strong>{rows.length}</strong> total rows
              </span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f8f7fc",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    state: {
      isLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: "#ece8fd15",
        },
      },
      className: "hoverable-row",
    },
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${
          table.refs.topToolbarRef.current?.offsetHeight || 70
        }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
      },
    }),
    renderEmptyRowsFallback: () => {
      return isLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
            New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return <MaterialReactTable table={table} />;
};

export default HealthStatusTable;
